import { useState, FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/common/layout/Header";
import Footer from "../../components/common/layout/Footer";
import { GradientButton } from "../../components/common/ui/Button";
import { Mail, AlertCircle, CheckCircle2, Loader2, KeyRound, ArrowLeft, ShieldCheck } from "lucide-react";
import { API_URL } from "../../config/api";

type ResetStep = 'IDENTIFY' | 'OTP' | 'RESET' | 'SUCCESS';

export default function ForgotPassword() {
  const navigate = useNavigate();

  // State
  const [step, setStep] = useState<ResetStep>('IDENTIFY');
  const [identifier, setIdentifier] = useState<string>("");
  const [otp, setOtp] = useState<string>("");
  const [email, setEmail] = useState<string>(""); // REAL email for API calls
  const [maskedEmail, setMaskedEmail] = useState<string>(""); // Only for UI
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  // Phase 1: Request OTP
  const handleRequestOTP = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/auth/request-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier: identifier.trim() }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Identification failed.");
      }

      setEmail(data.email || identifier.trim());
      setMaskedEmail(data.maskedEmail || identifier.trim());
      setStep('OTP');
      setMessage(`A 6-digit verification code has been sent to ${data.maskedEmail || 'your registered email'}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Request failed.");
    } finally {
      setLoading(false);
    }
  };

  // Phase 2: Verify OTP
  const handleVerifyOTP = async (e: FormEvent) => {
    e.preventDefault();
    if (otp.length !== 6) {
      setError("Please enter a valid 6-digit code.");
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/auth/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email, otp: otp.trim() }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Invalid or expired OTP.");
      }

      setStep('RESET');
      setMessage(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Verification failed.");
    } finally {
      setLoading(false);
    }
  };

  // Phase 3: Final Reset
  const handleResetPassword = async (e: FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email,
          otp: otp.trim(),
          newPassword: newPassword.trim()
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Password reset failed.");
      }

      setStep('SUCCESS');
      setTimeout(() => navigate("/login"), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Reset failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen text-white relative overflow-hidden flex flex-col">
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a0a1a] via-[#1a1a3e] to-[#0a0a1a]" />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-purple-600/10 blur-[120px] rounded-full" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-600/10 blur-[120px] rounded-full" />
      </div>

      <Header />

      <div className="flex-1 container pt-12 pb-20 px-4 relative z-10 flex items-center justify-center">
        <div className="max-w-md w-full">
          <div className="bg-white/[0.03] backdrop-blur-2xl rounded-3xl p-8 md:p-10 border border-white/10 shadow-[0_8px_32px_0_rgba(0,0,0,0.36)]">

            {/* Progress indicator */}
            <div className="flex justify-between mb-10 px-4">
              {[1, 2, 3].map((s) => (
                <div key={s} className="flex flex-col items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${(step === 'IDENTIFY' && s === 1) ||
                    (step === 'OTP' && s === 2) ||
                    (step === 'RESET' && s === 3) ||
                    (step === 'SUCCESS')
                    ? 'bg-gradient-to-r from-purple-500 to-blue-500 scale-110 shadow-lg shadow-purple-500/20'
                    : s < (step === 'IDENTIFY' ? 1 : step === 'OTP' ? 2 : 3)
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-white/5 text-white/40'
                    }`}>
                    {s < (step === 'IDENTIFY' ? 1 : step === 'OTP' ? 2 : 3) || step === 'SUCCESS' ? <ShieldCheck className="w-4 h-4" /> : s}
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold mb-2 bg-gradient-to-r from-white to-white/60 bg-clip-text text-transparent">
                {step === 'IDENTIFY' && "Forgot Password"}
                {step === 'OTP' && "Verify Identity"}
                {step === 'RESET' && "New Password"}
                {step === 'SUCCESS' && "All Set!"}
              </h1>
              <p className="text-white/60 text-sm">
                {step === 'IDENTIFY' && "Enter your email, username, or customer ID to begin."}
                {step === 'OTP' && "Enter the 6-digit code we sent to your email."}
                {step === 'RESET' && "Create a strong new password for your account."}
                {step === 'SUCCESS' && "Your password has been reset successfully."}
              </p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-2xl flex items-center gap-3 text-red-400 animate-in fade-in slide-in-from-top-2">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm font-medium">{error}</span>
              </div>
            )}

            {message && (
              <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-2xl flex items-center gap-3 text-blue-300 animate-in fade-in slide-in-from-top-2">
                <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm font-medium">{message}</span>
              </div>
            )}

            {/* Step 1: Identification */}
            {step === 'IDENTIFY' && (
              <form onSubmit={handleRequestOTP} className="space-y-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-white/80 ml-1">Account Identifier</label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors group-focus-within:text-purple-400 text-white/40">
                      <Mail className="w-5 h-5" />
                    </div>
                    <input
                      type="text"
                      value={identifier}
                      onChange={(e) => setIdentifier(e.target.value)}
                      className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 text-white placeholder-white/20 transition-all"
                      placeholder="Email, Username or ID"
                      required
                    />
                  </div>
                </div>
                <GradientButton type="submit" disabled={loading} className="w-full py-4 rounded-2xl">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Continue"}
                </GradientButton>
                <div className="text-center">
                  <button type="button" onClick={() => navigate("/login")} className="text-sm text-white/40 hover:text-white transition-colors inline-flex items-center gap-2">
                    <ArrowLeft className="w-4 h-4" /> Back to Login
                  </button>
                </div>
              </form>
            )}

            {/* Step 2: OTP Verification */}
            {step === 'OTP' && (
              <form onSubmit={handleVerifyOTP} className="space-y-6">
                <div className="space-y-2">
                  <div className="flex justify-between items-center ml-1">
                    <label className="text-sm font-medium text-white/80">Verification Code</label>
                    <span className="text-[10px] text-white/40 uppercase tracking-wider">Sent to {maskedEmail}</span>
                  </div>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-purple-400">
                      <KeyRound className="w-5 h-5" />
                    </div>
                    <input
                      type="text"
                      maxLength={6}
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                      className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/50 text-center text-2xl tracking-[12px] font-mono text-white transition-all underline decoration-purple-500/50 underline-offset-8"
                      placeholder="000000"
                      required
                    />
                  </div>
                </div>
                <GradientButton type="submit" disabled={loading} className="w-full py-4 rounded-2xl">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Verify Code"}
                </GradientButton>
                <div className="flex justify-between">
                  <button type="button" onClick={() => setStep('IDENTIFY')} className="text-sm text-white/40 hover:text-white transition-colors">
                    Change identifier
                  </button>
                  <button type="button" onClick={handleRequestOTP} className="text-sm text-purple-400 hover:text-purple-300 font-medium transition-colors">
                    Resend Code
                  </button>
                </div>
              </form>
            )}

            {/* Step 3: New Password */}
            {step === 'RESET' && (
              <form onSubmit={handleResetPassword} className="space-y-6">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-white/80 ml-1">New Password</label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full px-4 py-4 bg-white/5 border border-white/10 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/50 text-white transition-all"
                      placeholder="••••••••"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-white/80 ml-1">Confirm New Password</label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full px-4 py-4 bg-white/5 border border-white/10 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/50 text-white transition-all"
                      placeholder="••••••••"
                      required
                    />
                  </div>
                </div>
                <GradientButton type="submit" disabled={loading} className="w-full py-4 rounded-2xl">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Update Password"}
                </GradientButton>
              </form>
            )}

            {/* Success State */}
            {step === 'SUCCESS' && (
              <div className="text-center py-10 space-y-6">
                <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto scale-in shadow-[0_0_40px_rgba(34,197,94,0.2)]">
                  <ShieldCheck className="w-10 h-10 text-green-400" />
                </div>
                <div className="space-y-2">
                  <h2 className="text-2xl font-bold">Password Updated!</h2>
                  <p className="text-white/40">Redirecting you to login in a moment...</p>
                </div>
                <GradientButton onClick={() => navigate("/login")} className="w-full py-4 rounded-2xl">
                  Go to Login Now
                </GradientButton>
              </div>
            )}

          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
